import React from 'react';
import { Phone, PhoneCall, Timer, Video } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { Card } from './premium';
import { fmtMinutes, periodLabel } from './teamUi';

// Calls (Nic 2026-10-25): its own card on each team tab, both read from
// RingCentral's call log on their own. Up front, the first call length (and,
// for Onboarding, the time to call back a missed hot seat, what Jorge asked
// for) beside ONE bar that is every call, split real / short / didn't
// connect (Nic 2026-11-02: eight equal tiles weighed "65 calls" and "0 video
// meetings" the same, and the three outcomes never added up in front of
// you). Then the two conversation lengths and the connect rate as quiet
// stats, and meetings held as a footnote until there are some. Video
// meetings are counted; their length isn't tracked yet.

// 450 -> "7m 30s", 75 -> "1m 15s", 40 -> "40s".
export const fmtSeconds = (s) => {
  if (s == null) return '-';
  const n = Math.round(Number(s) || 0);
  const m = Math.floor(n / 60);
  return m ? `${m}m ${String(n % 60).padStart(2, '0')}s` : `${n}s`;
};

const pctOf = (n, d) => (d ? Math.round((n / d) * 100) : 0);

const Big = ({ icon: Icon, label, value, sub, info, tone }) => (
  <div className="min-w-0" title={info}>
    <div
      className={cn(
        'flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider',
        tone || 'text-slate-400',
      )}
    >
      <Icon size={13} className="shrink-0" />
      {label}
    </div>
    <div className="mt-1.5 text-[26px] font-semibold leading-none tabular-nums text-slate-900 dark:text-white">
      {value}
    </div>
    {sub ? (
      <div className="mt-1.5 text-[11.5px] leading-snug text-slate-500 dark:text-slate-400">
        {sub}
      </div>
    ) : null}
  </div>
);

// The outcome bar: every call, split by what happened.
const OutcomeBar = ({ calls }) => {
  const total = calls.total || 0;
  const real = calls.real || 0;
  const short = calls.short || 0;
  const none = calls.notConnected || 0;
  const w = (n) => `${total ? (n / total) * 100 : 0}%`;
  return (
    <div className="min-w-0">
      <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
        <b className="font-semibold tabular-nums text-slate-900 dark:text-white">
          {total.toLocaleString('en-US')} call{total === 1 ? '' : 's'}
        </b>{' '}
        this period
      </div>
      <div
        className="mt-2 flex h-3.5 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10"
        role="img"
        aria-label={`${real} real conversations, ${short} short, ${none} didn't connect`}
      >
        <span className="bg-emerald-500" style={{ width: w(real) }} />
        <span className="bg-amber-500" style={{ width: w(short) }} />
        <span className="bg-slate-300 dark:bg-white/20" style={{ width: w(none) }} />
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="whitespace-nowrap">
          <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-emerald-500 align-[-1px]" />
          <b className="font-semibold tabular-nums text-slate-900 dark:text-white">{real}</b> real conversation{real === 1 ? '' : 's'}
          <span className="tabular-nums"> · {pctOf(real, total)}%</span>
        </span>
        <span className="whitespace-nowrap">
          <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-amber-500 align-[-1px]" />
          <b className="font-semibold tabular-nums text-slate-900 dark:text-white">{short}</b> short
          <span className="tabular-nums"> · {pctOf(short, total)}%</span>
        </span>
        <span className="whitespace-nowrap">
          <i className="mr-1.5 inline-block h-2 w-2 rounded-sm bg-slate-300 align-[-1px] dark:bg-white/20" />
          <b className="font-semibold tabular-nums text-slate-900 dark:text-white">{none}</b> didn&apos;t connect
          <span className="tabular-nums"> · {pctOf(none, total)}%</span>
        </span>
      </div>
    </div>
  );
};

const Stat = ({ value, label, hi = false }) => (
  <div
    className={cn(
      'rounded-xl px-3.5 py-3',
      hi ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-slate-50 dark:bg-white/5',
    )}
  >
    <div
      className={cn(
        'text-lg font-semibold leading-none tabular-nums',
        hi ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-white',
      )}
    >
      {value}
    </div>
    <div className="mt-1.5 text-[11px] leading-snug text-slate-400">{label}</div>
  </div>
);

const CallNumbers = ({ calls, team }) => {
  const f = calls.firstCall || {};
  const cb = calls.callBack || {};
  const m = calls.meetings || {};
  const sales = team === 'sales';
  const who = sales ? 'new leads' : 'clients signed';
  const total = calls.total || 0;
  const connected = calls.connected ?? total - (calls.notConnected || 0);
  const held = (m.phone || 0) + (m.video || 0);
  return (
    <div className="space-y-3">
      <div
        className={cn(
          'grid gap-x-6 gap-y-4 rounded-xl bg-slate-50 px-4 py-4 dark:bg-white/5',
          sales ? 'lg:grid-cols-[minmax(0,17rem)_1fr] lg:items-center' : 'lg:grid-cols-2',
        )}
      >
        <Big
          icon={PhoneCall}
          label="First call length"
          tone="text-emerald-600 dark:text-emerald-400"
          value={fmtSeconds(f.medianSeconds)}
          sub={
            f.of
              ? `Median, from ${f.found} of ${f.of} ${who}`
              : `No ${who} in this period`
          }
          info={
            sales
              ? "Each new lead's first real conversation with their rep after their clock started: a connected call of 2 minutes or more. Leads never reached on a real call aren't in it."
              : "Each client's first real conversation with their specialist after signing: a connected call of 2 minutes or more. Clients with no such call (a video first call, a personal cell) aren't in it."
          }
        />
        {sales ? null : (
          <Big
            icon={Timer}
            label="Time to call back a missed hot seat"
            value={cb.calledBack ? fmtMinutes(cb.medianMinutes) : '-'}
            sub={
              cb.missed
                ? `Median · ${cb.calledBack} of ${cb.missed} called back${cb.waiting ? ` · ${cb.waiting} still waiting` : ''}`
                : 'No missed hot seats in this period'
            }
            info="From the missed transfer to that specialist's first connected call with the client."
          />
        )}
        <div className={cn(!sales && 'lg:col-span-2')}>
          <OutcomeBar calls={calls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat value={fmtSeconds(calls.medianSeconds)} label="Typical real conversation (median)" hi />
        <Stat value={fmtSeconds(calls.avgSeconds)} label="Average real conversation" />
        <Stat
          value={total ? `${pctOf(connected, total)}%` : '-'}
          label={total ? `Calls that connected (${connected} of ${total})` : 'Calls that connected'}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-slate-400">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
          {held
            ? `Meetings held: ${m.phone || 0} phone · ${m.video || 0} video`
            : 'Meetings held: none yet'}
        </span>
        <span>video length not tracked yet</span>
      </div>
    </div>
  );
};

const CallsCard = ({ calls, team, period }) => {
  if (!calls) return null;
  return (
    <Card
      eyebrow={periodLabel(period)}
      title={
        <span className="inline-flex items-center gap-1.5">
          <Phone size={15} className="text-slate-400" />
          Calls
          <Video size={15} className="text-slate-400" />
        </span>
      }
      info={`Every call with ${team === 'sales' ? 'a lead' : 'a client'}, read from RingCentral on its own. A real conversation is a connected call of 2 minutes or more.`}
    >
      <CallNumbers calls={calls} team={team} />
    </Card>
  );
};

export default CallsCard;
